// Canonical integer (no leading zeros), optionally negative: "0", "1", "124", "-1", "-12" — but NOT
// "-0", "007", "1a", "" or "Infinity"/"1e3" (which `Number()` would otherwise turn into a number). A
// path segment matching this is an index / numeric key, never an intentional string key. Negative
// values matter because the annotator addresses unsaved placeholder elevations by negative id
// (`elevations.byId[-1]`); without coercion the `<Form>` would bind to the string key "-1" and miss
// the number-keyed record entirely.
const NUMERIC_INDEX = /^(0|-?[1-9]\d*)$/;

/**
 * Splits a dotted form-field `name` into an Immutable `getIn`/`setIn` path, coercing canonical
 * integer segments to numbers so the path can address number-keyed collections.
 *
 * Immutable `List` already coerces a numeric-string index (`wrapIndex`), so for the app's existing
 * list-backed forms (`payment_term_items.0.amount`, `recipients.0.email`, …) this is a no-op. The
 * coercion additionally lets a segment address a number-keyed `Map` — e.g. `elevations.byId[5]` or
 * the annotator's placeholder `elevations.byId[-1]` — which `Map.get("5")` (exact-key) would
 * otherwise miss. That is what lets a single `<Form>` bind to a nested annotation row at
 * `["elevations","byId", 5, "annotations","list", 0, "width"]`.
 *
 * @param {string} name The dotted field name (e.g. "5.annotations.list.0.width")
 * @returns {(string | number)[]} the path with integer segments coerced to numbers
 */
export const parsePath = (name: string): (string | number)[] =>
  name.split(".").map((segment) => (NUMERIC_INDEX.test(segment) ? Number(segment) : segment));
